import React, { useState } from 'react';
import {
  X,
  Minimize2,
  Maximize2,
  Calendar,
  Clock,
  User,
  ShoppingBag,
  Sparkles,
  CreditCard,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../common/Button';
import { Drawer } from '../common/Drawer';
import { AppointmentOptionsMenu } from './AppointmentOptionsMenu';
import { useBaseCurrency, useStaff } from '../../db/hooks/useLookupData';
import { usePOSStore } from '../../stores/pos.store';
import { dayjs } from '../../lib/dayjs';
import type { ServiceDocType, CustomerDocType, PriceTier } from '../../db/schemas';

export interface AppointmentDrawerData {
  id: string;
  customerId?: string;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  service: ServiceDocType;
  operatorId?: string | null;
  selectedTier?: PriceTier | null;
  dateTime?: string;
  fechaHora: string; // ISO string
  durationMinutes?: number;
  duracionMinutos?: number;
  status: 'booked' | 'completed' | 'no_show' | 'cancelled';
  amount: number;
  transactionId?: string;
  notes?: string;
}

interface AppointmentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: AppointmentDrawerData | null;
  onViewClientProfile?: (customerId: string) => void;
  onViewInvoice?: (transactionId: string) => void;
  onReschedule?: (appointmentId: string) => void;
  onMarkNoShow?: (appointmentId: string) => void;
  onCancelAppointment?: (appointmentId: string) => void;
}

export function AppointmentDrawer({
  isOpen,
  onClose,
  appointment,
  onViewClientProfile,
  onViewInvoice,
  onReschedule,
  onMarkNoShow,
  onCancelAppointment,
}: AppointmentDrawerProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const { staff = [] } = useStaff();
  const openPOS = usePOSStore((s) => s.open);
  const addServiceToCart = usePOSStore((s) => s.addServiceToCart);

  if (!isOpen || !appointment) return null;

  const assignedStaff = staff.find((p) => p.id === appointment.operatorId);
  const isPaid = appointment.status === 'completed' && Boolean(appointment.transactionId);
  const dateFormatted = dayjs(appointment.fechaHora).format('ddd D MMM, YYYY');
  const timeFormatted = dayjs(appointment.fechaHora).format('HH:mm');

  const handleFinalizarCompra = () => {
    openPOS({
      id: appointment.customerId || '',
      name: appointment.customerName,
      email: appointment.customerEmail || '',
      phone: appointment.customerPhone || '',
      created_at: '',
      updated_at: '',
      is_deleted: false,
    } as CustomerDocType);

    addServiceToCart(
      appointment.service,
      appointment.operatorId || null,
      appointment.selectedTier || null
    );

    onClose();
  };

  const getStatusBadge = () => {
    switch (appointment.status) {
      case 'completed':
        return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
      case 'no_show':
        return 'bg-amber-500/10 text-amber-500 border-amber-500/20';
      case 'cancelled':
        return 'bg-rose-500/10 text-rose-500 border-rose-500/20';
      default:
        return 'bg-brand-sky/10 text-brand-sky border-brand-sky/20';
    }
  };

  // ── Minimized Bottom Floating Dock Bar ──
  if (isMinimized) {
    return (
      <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 z-[70] animate-scale-in">
        <div className="flex items-center justify-between gap-3 bg-surface/95 backdrop-blur-xl border border-brand-sky/30 p-3 px-4 rounded-2xl shadow-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-3 h-3 rounded-full bg-brand-sky animate-pulse shrink-0" />
            <div className="min-w-0">
              <p className="text-xs font-bold text-text-primary truncate">
                Cita: {appointment.customerName}
              </p>
              <p className="text-[10px] text-text-secondary truncate">
                {appointment.service.concept} • {currencySymbol}{appointment.amount}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 ml-2 border-l border-border pl-2 shrink-0">
            <button
              onClick={() => setIsMinimized(false)}
              className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl hover:bg-surface-secondary text-text-secondary hover:text-white transition-colors cursor-pointer"
              title="Expandir cajón"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl hover:bg-surface-secondary text-text-tertiary hover:text-text-primary transition-colors cursor-pointer"
              title="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Detalles de Cita — ${appointment.customerName}`}
      subtitle={`${dateFormatted} • ${timeFormatted}`}
      icon={Calendar}
      maxWidth="lg"
      zIndexClass="z-[70]"
      footer={
        <div className="w-full flex items-center justify-between gap-3">
          <AppointmentOptionsMenu
            status={appointment.status}
            onReschedule={() => onReschedule?.(appointment.id)}
            onMarkNoShow={() => onMarkNoShow?.(appointment.id)}
            onCancel={() => onCancelAppointment?.(appointment.id)}
            onRefund={() => console.log('Reembolso solicitado')}
          />

          {!isPaid ? (
            <Button
              variant="primary"
              size="md"
              icon={ShoppingBag}
              onClick={handleFinalizarCompra}
              className="flex-1 bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold shadow-lg shadow-brand-orange/20"
            >
              Finalizar compra
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="md"
              icon={CreditCard}
              onClick={() => appointment.transactionId && onViewInvoice?.(appointment.transactionId)}
              className="flex-1 border-brand-sky/40 text-brand-sky hover:bg-brand-sky/10"
            >
              Ver recibo de venta
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Status Badge Row */}
        <div className="flex items-center gap-2">
          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-extrabold capitalize border ${getStatusBadge()}`}>
            {appointment.status}
          </span>
        </div>

        {/* Customer Profile Card */}
        <div className="bg-surface-secondary/50 border border-border/80 rounded-2xl p-4 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-brand-sky/10 border border-brand-sky/30 flex items-center justify-center text-brand-sky font-extrabold text-base shrink-0">
                {appointment.customerName.charAt(0).toUpperCase()}
              </div>
              <div>
                <h4 className="font-extrabold text-sm text-text-primary">
                  {appointment.customerName}
                </h4>
                <p className="text-xs text-text-secondary">
                  {appointment.customerEmail || appointment.customerPhone || 'Sin contacto directo'}
                </p>
              </div>
            </div>
            
            {onViewClientProfile && appointment.customerId && (
              <Button
                variant="ghost"
                size="xs"
                icon={ExternalLink}
                onClick={() => onViewClientProfile(appointment.customerId!)}
                className="hover:text-brand-sky font-bold"
              >
                Perfil
              </Button>
            )}
          </div>
        </div>

        {/* Service & Operator Section */}
        <div className="space-y-3">
          <h4 className="text-xs font-extrabold uppercase tracking-wider text-text-tertiary flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-brand-orange" />
            Servicio Reservado
          </h4>

          <div className="bg-surface border border-border p-4 rounded-2xl shadow-xs space-y-3">
            <div className="flex justify-between items-start">
              <div>
                <h5 className="font-bold text-sm text-text-primary">
                  {appointment.service.concept}
                </h5>
                {appointment.selectedTier && (
                  <span className="inline-block mt-1 text-[11px] font-bold text-brand-orange bg-brand-orange/10 px-2 py-0.5 rounded-md border border-brand-orange/20">
                    Oferta: {appointment.selectedTier.label}
                  </span>
                )}
              </div>

              <div className="text-right">
                <span className="font-extrabold text-base text-text-primary">
                  {appointment.amount === 0 ? '€0.00' : `${currencySymbol}${appointment.amount.toFixed(2)}`}
                </span>
                <span className="block text-[10px] text-text-tertiary">
                  {appointment.amount === 0 ? 'Canje de Paquete' : baseCurrency}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-border/50 text-xs text-text-secondary">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-brand-sky" />
                  {timeFormatted} ({appointment.durationMinutes || appointment.duracionMinutos || 30} min)
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5 text-brand-orange" />
                  {assignedStaff ? assignedStaff.name : 'Sin esteticista asignado'}
                </span>
              </div>

              {appointment.amount === 0 && (
                <span className="text-[11px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                  Incluido en Paquete
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Financial Totals */}
        <div className="bg-surface-secondary/40 border border-border rounded-2xl p-4 space-y-2">
          <div className="flex justify-between text-xs text-text-secondary">
            <span>Subtotal Cita</span>
            <span>{currencySymbol}{appointment.amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm font-extrabold text-text-primary pt-2 border-t border-border/60">
            <span>Total A Pagar</span>
            <span className="text-brand-orange">
              {appointment.amount === 0 ? '€0.00 EUR (Paquete Prepagado)' : `${currencySymbol}${appointment.amount.toFixed(2)} ${baseCurrency}`}
            </span>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
